import React from 'react';
import { Text } from '@/hen-ui/atomics';
import { cn } from '@/hen-ui/utils';
import { footerVariants, type FooterVariantProps } from './footer.variants';

/**
 * Props for the Brand component
 *
 * Brand is a sub-component of Footer that displays the company logo and tagline.
 * It uses the Text atomic component for consistent typography.
 */
const brandTaglineClassName =
  'max-w-full md:max-w-footer break-words [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-90';

type BrandProps = {
  /** Logo element to display (can be an image, SVG, or any React node) */
  logo: React.ReactNode;
  /** Tagline / brand copy below the logo */
  children?: React.ReactNode;
  /** Class name to apply to the brand section */
  className?: string;
};

/**
 * Brand - Displays the company/brand logo and tagline in the footer
 *
 * This sub-component is part of the Footer compound component pattern.
 * It provides a consistent way to display brand identity in footers.
 * Responsive: full width on mobile, fit-content on medium screens and above.
 * Tagline text wraps properly with responsive max-width constraints.
 *
 * Pass **children** for the tagline: plain text, or composed React nodes (e.g.
 * `Link` for inline actions). HTML-from-strings stays the app’s concern.
 *
 * @param logo - The logo element to display (Image, SVG, or any React node)
 * @param children - Tagline content below the logo
 */
const Brand = ({ logo, children, className }: BrandProps) => (
  <section
    className={cn(
      'flex md:min-w-footer-section gap-lg flex-col w-full md:w-fit',
      className,
    )}
  >
    {logo}
    {children ? (
      <Text className={brandTaglineClassName} variant='b4' color='white'>
        {children}
      </Text>
    ) : null}
  </section>
);
Brand.displayName = 'Footer.Brand';
export { Brand as FooterBrand };

/**
 * SectionGroup - Container for grouping multiple Section components together
 *
 * This sub-component provides flexible layout with minimum width and spacing
 * between sections. Sections wrap on smaller screens and display horizontally
 * on larger screens. Items are aligned to the end (right) of the container.
 *
 * @param children - Section components to be grouped together (Footer.Section elements)
 * @param className - Class name to apply to the section group section
 */
type SectionGroupProps = React.PropsWithChildren<{
  /** Class name to apply to the section group section */
  className?: string;
}>;

const SectionGroup: React.FC<SectionGroupProps> = ({ children, className }) => (
  <section
    className={cn(
      'basis-full lg:flex-1 flex min-w-footer-section max-w-footer-section-group mx-auto lg:mx-0 justify-end md:justify-start lg:justify-end xl:justify-between items-start gap-2xl flex-wrap md:flex-nowrap',
      className,
    )}
  >
    {children}
  </section>
);
SectionGroup.displayName = 'Footer.SectionGroup';
export { SectionGroup as FooterSectionGroup };

/**
 * Props for the Section component
 *
 * Section is a sub-component that creates a titled section within the footer,
 * typically used to organize navigation links or information.
 */
type SectionProps = React.PropsWithChildren<{
  /** Title text for the section (e.g., "Company", "Resources", "Legal") */
  title: string;
  /** Class name to apply to the section */
  className?: string;
}>;

/**
 * Section - A footer section with a title and content
 *
 * This sub-component creates a titled section within the footer, typically used
 * to organize related links or information. Use within Footer.SectionGroup to
 * create multiple columns of navigation. Responsive: full width on mobile,
 * flex behavior on medium screens and above to allow wrapping.
 *
 * @param title - The section title displayed as bold text
 * @param children - Content to display below the title (typically Link components)
 */
const Section: React.FC<SectionProps> = ({ title, children, className }) => (
  <div
    className={cn('gap-sm md:gap-xs flex flex-col w-full md:w-fit', className)}
  >
    <Text variant='b5' className='font-bold whitespace-nowrap' color='white'>
      {title}
    </Text>
    {children}
  </div>
);
Section.displayName = 'Footer.Section';
export { Section as FooterSection };

/**
 * Links - Container for displaying navigation links in a flexible layout
 *
 * This sub-component provides a horizontal layout for links, typically used
 * in the Footer.Bottom section for legal links, social media, or other
 * secondary navigation.
 *
 * @param children - Link elements to display (typically Link atomic components or anchor tags)
 * @param className - Class name to apply to the links section
 */
type LinksProps = React.PropsWithChildren<{
  /** Class name to apply to the links section */
  className?: string;
}>;

const Links: React.FC<LinksProps> = ({ children, className }) => (
  <div
    className={cn(
      'flex justify-between items-start gap-x-xl self-stretch flex-wrap',
      className,
    )}
  >
    {children}
  </div>
);
Links.displayName = 'Footer.Links';
export { Links as FooterLinks };

/**
 * Top - Container for the top section of the footer
 *
 * This sub-component provides the main content area of the footer with
 * flexible layout and maximum desktop width constraint. Typically contains
 * Brand and SectionGroup components. A Grid or Heading child takes the full
 * row so a titled multi-column band can be composed without `className`.
 * The match reaches through up to two `display: contents` wrappers, which the
 * Dynamic Experiences runtime adds for tracking and breakpoint visibility.
 * Headings match on `font-heading`, so `tag='p'` Headings count too.
 *
 * @param children - Content for the top section (usually Footer.Brand and Footer.SectionGroup)
 * @param className - Class name to apply to the top section
 */
type TopProps = React.PropsWithChildren<{
  /** Class name to apply to the top section */
  className?: string;
}>;

const Top: React.FC<TopProps> = ({ children, className }) => (
  <div
    className={cn(
      'self-stretch flex-wrap items-start justify-between flex gap-y-xl gap-x-lg',
      '[&>:is(.grid,.font-heading)]:basis-full',
      '[&>*>:is(.grid,.font-heading)]:basis-full',
      '[&>*>*>:is(.grid,.font-heading)]:basis-full',
      className,
    )}
  >
    {children}
  </div>
);
Top.displayName = 'Footer.Top';
export { Top as FooterTop };

/**
 * Bottom - Container for the bottom section of the footer
 *
 * This sub-component provides the bottom section of the footer with a horizontal
 * divider line and flexible content layout. Typically used for copyright information,
 * legal links, and social media icons. For centered or stacked legal rows, pass a
 * full-width Flex or Grid of Links instead of Footer.Links.
 *
 * @param children - Content for the bottom section (Text, Footer.Links, or a Flex/Grid of Links)
 * @param className - Class name to apply to the bottom section
 */
type BottomProps = React.PropsWithChildren<{
  /** Class name to apply to the bottom section */
  className?: string;
}>;

const Bottom: React.FC<BottomProps> = ({ children, className }) => (
  <>
    <div className='bg-neutral-500 w-full h-[1px]' />
    <div
      className={cn(
        'flex justify-between items-start gap-x-xl self-stretch flex-wrap bg-surface-footerLegal',
        className,
      )}
    >
      {children}
    </div>
  </>
);
Bottom.displayName = 'Footer.Bottom';
export { Bottom as FooterBottom };

export interface FooterProps extends FooterVariantProps {
  /** Footer content composed using sub-components (Footer.Top, Footer.Bottom, etc.) */
  children?: React.ReactNode;
  /** Class name to apply to the footer */
  className?: string;
}

/**
 * Footer - A compound component for building flexible, accessible website footers
 *
 * This composite component follows the compound component pattern, providing
 * sub-components that can be composed together to create various footer layouts
 * while maintaining design consistency. It is composed entirely of atomic components
 * (Text) and uses HEN Design System tokens for all styling.
 *
 * ## Component Composition
 * - Uses `Text` atomic component for tagline and copyright text
 * - All styling uses design tokens from tailwind.config.ts
 * - Semantic HTML with `<footer>` element for accessibility
 *
 * ## Variants
 * - **default**: Full footer that composes the sub-components below.
 * - **basic**: Compact single-line footer (Figma "Footer / Basic") with XL
 *   top/bottom padding and footer-sides left/right padding. Pass a single
 *   centered `Text` child for the copyright/disclosure line (same as how
 *   copyright text is composed inside `Footer.Bottom`).
 *
 * ## Sub-Components
 * - **Footer.Top**: Main content area (brand + navigation sections)
 * - **Footer.Bottom**: Bottom section (copyright + legal links)
 * - **Footer.Brand**: Logo and tagline display
 * - **Footer.SectionGroup**: Groups multiple sections horizontally
 * - **Footer.Section**: Individual titled section with links
 * - **Footer.Links**: Horizontal layout for links
 *
 * ## Use Cases
 * - Full-featured website footers with navigation
 * - Minimal footers with just copyright
 * - Marketing site footers with social links
 * - Application footers with legal information
 * - Compact flow footers with only a centered copyright line (`variant="basic"`)
 *
 * ## Responsive Behavior
 * - Sections stack vertically on mobile and wrap on medium screens
 * - Sections display horizontally with justify-between spacing at xl breakpoint (1440px) and above
 * - Brand and tagline are full width on mobile, constrained on larger screens
 * - Images are constrained to max 200px width on mobile
 *
 * @example
 * ```tsx
 * <Footer>
 *   <Footer.Top>
 *     <Footer.Brand logo={<Logo />}>Your trusted education partner</Footer.Brand>
 *     <Footer.Brand logo={<Logo />}>
 *       Questions? <Link href="/contact" variant="white" size="sm">Contact us</Link>
 *     </Footer.Brand>
 *     <Footer.SectionGroup>
 *       <Footer.Section title="Company">
 *         <Link href="/about">About</Link>
 *         <Link href="/careers">Careers</Link>
 *       </Footer.Section>
 *     </Footer.SectionGroup>
 *   </Footer.Top>
 *   <Footer.Bottom>
 *     <Text variant="b5">© 2026 Company Name</Text>
 *     <Footer.Links>
 *       <Link href="/privacy">Privacy</Link>
 *       <Link href="/terms">Terms</Link>
 *     </Footer.Links>
 *   </Footer.Bottom>
 * </Footer>
 * ```
 *
 * @param children - Footer content composed using sub-components (Footer.Top, Footer.Bottom, etc.)
 */
function FooterRoot({ children, className, variant }: FooterProps) {
  return (
    <footer className={cn(footerVariants({ variant }), className)}>
      {children}
    </footer>
  );
}
FooterRoot.displayName = 'Footer';

/** Ensures `Footer.Brand` (etc.) keep correct prop types after `Object.assign`. */
type FooterCompound = typeof FooterRoot & {
  Brand: typeof Brand;
  SectionGroup: typeof SectionGroup;
  Section: typeof Section;
  Links: typeof Links;
  Bottom: typeof Bottom;
  Top: typeof Top;
};

export const Footer: FooterCompound = Object.assign(FooterRoot, {
  Brand,
  SectionGroup,
  Section,
  Links,
  Bottom,
  Top,
}) as FooterCompound;
